import type { FunctionComponent, PropsWithChildren } from "react"

import { useParams, usePathname, useSearchParams } from "next/navigation"
import { useEffect, useState } from "react"

import { MenuBarLink } from "@jobteaser/ui-kit/components/MenuBar/Link/MenuBarLink"
import { MenuBar } from "@jobteaser/ui-kit/components/MenuBar/MenuBar"

import { useCompanyBoostedArticlesFetch } from "@/modules/companies/hooks/useCompanyBoostedArticlesFetch"
import { useCompanyFetch } from "@/modules/companies/hooks/useCompanyFetch"
import { COMPANY_BASENAME, getCompanyRoutes, NEWSFEED_PAGE_ROUTE } from "@/modules/companies/utils/routing"
import { useScrollIntoView } from "@/modules/dom/hooks/useScrollIntoView"
import { useTranslation } from "@/modules/i18n/components/useTranslation"
import { JOB_ADS_CONTENT_BOOST } from "@/modules/jobAds/client/constants/flags"
import { useJobAdsFromAlgolia } from "@/modules/jobAds/client/hooks/useJobAdsFromAlgolia"
import { useLocale } from "@/modules/locales/useLocale"
import { Link } from "@/modules/routing/components/Link"
import { useFlag } from "@/modules/unleash/hooks/useFlag"

import styles from "./Menu.module.css"

export const Menu: FunctionComponent<PropsWithChildren> = () => {
  const [jobAdsNumber, setJobAdsNumber] = useState<number>(0)
  const pathname = usePathname()
  const { articleSlug, companySlug } = useParams()
  const searchParams = useSearchParams()
  const previewURL = searchParams.get("preview_url")
  const { t } = useTranslation(["companies_client"])
  const locale = useLocale()
  const isJobAdsContentBoostActive = useFlag(JOB_ADS_CONTENT_BOOST)

  const { dataCompany } = useCompanyFetch()
  const { dataCompanyBoostedArticles } = useCompanyBoostedArticlesFetch()
  const { dataJobAds } = useJobAdsFromAlgolia()

  const scrollToArticle = searchParams.get("scrollToArticle")
  const { scrollToElement } = useScrollIntoView()

  const ARTICLE_MENU_ID = "article_menu_id"

  const translationVariableMap: Record<string, number> = {
    jobAdsNumber,
  }

  useEffect(() => {
    if (scrollToArticle) {
      scrollToElement(`#${ARTICLE_MENU_ID}`)
    }
  }, [scrollToArticle, scrollToElement])

  useEffect(() => {
    if (dataJobAds) {
      setJobAdsNumber(dataJobAds.totalSize || 0)
    }
  }, [dataJobAds])

  return (
    <div className={styles.main} id={ARTICLE_MENU_ID}>
      <MenuBar labelButtonShowAll={t("companies_client.menu.show_all")}>
        {getCompanyRoutes(dataCompany, isJobAdsContentBoostActive && !!dataCompanyBoostedArticles?.articles.length).map(
          route => {
            const { id, path, translationKey, translationVariable } = route
            const fullPath = `/${locale}/${COMPANY_BASENAME}/${companySlug}${path}${
              previewURL ? `?preview_url=${previewURL}` : ""
            }`

            let isSelected = false

            if (articleSlug) {
              isSelected = path === NEWSFEED_PAGE_ROUTE.path
            } else {
              isSelected = fullPath.split(/[?#]/)[0] === pathname
            }

            return (
              <MenuBarLink
                as={Link}
                data-testid={`company_menu_item company_menu_item_${id}`}
                href={fullPath}
                key={id}
                selected={isSelected}
              >
                {translationVariable
                  ? t(translationKey, {
                      count: translationVariableMap[translationVariable],
                      [translationVariable]: translationVariableMap[translationVariable],
                    })
                  : t(translationKey)}
              </MenuBarLink>
            )
          }
        )}
      </MenuBar>
    </div>
  )
}
